import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Transition } from '@headlessui/react';
import { RealEstateDto } from 'api/real-estate';
import { SearchIcon } from 'components/icons/Icons';
import { Loading } from 'components';
import { Sizes } from 'components/ui/Loader';
import { NsKey } from 'enums/NsKey';
import { RealEstateSearchBarItem } from 'features/subscription-process/home-insurance/components/real-estate/RealEstateSearchBarItem';
import { LightRealEstateResult } from 'features/subscription-process/home-insurance/components/real-estate/RealEstateSearchBar';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { FC, useCallback, useMemo, useRef, useEffect, useState } from 'react';

interface BorrowerBienSearchBarProps {
  biens: RealEstateDto[];
  isLoading?: boolean;
  excludeBienId?: string;
  placeholder?: string;
  className?: string;
  onSelect: (property: LightRealEstateResult) => void;
}

const transformToLight = (result: RealEstateDto): LightRealEstateResult | null => {
  if (!result.id) return null;
  return {
    id: result.id,
    name: result.name || `Bien ${result.id.substring(0, 8)}`,
    address: result.address,
    area: result.area,
    roomCount: result.roomCount,
    propertyType: result.propertyType?.toString(),
    charges: result.charges,
    ownersCount: result.owners?.length || 0,
    data: result
  };
};

const buildHaystack = (p: LightRealEstateResult): string => {
  const parts = [p.name, p.address?.line1, p.address?.zipCode, p.address?.city].filter(Boolean);
  return parts.join(' ').toLowerCase();
};

export const BorrowerBienSearchBar: FC<BorrowerBienSearchBarProps> = ({
  biens,
  isLoading = false,
  excludeBienId,
  placeholder = 'CONTRACTUALIZATION.REAL_ESTATE.SEARCH_PLACEHOLDER',
  className,
  onSelect
}) => {
  const { t } = useDynamicTranslation([NsKey.Contractualization, NsKey.RealEstate, NsKey.Common]);

  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const allResults = useMemo(
    () => biens.map(transformToLight).filter((r): r is LightRealEstateResult => r !== null),
    [biens]
  );

  const filteredResults = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return allResults
      .filter((r) => r.id !== excludeBienId)
      .filter((r) => (needle ? buildHaystack(r).includes(needle) : true));
  }, [allResults, excludeBienId, searchText]);

  const handleSelect = useCallback(
    (property: LightRealEstateResult | null) => {
      if (!property) return;
      onSelect(property);
      setSearchText('');
      setIsOpen(false);
    },
    [onSelect]
  );

  const displayValue = useCallback(() => searchText, [searchText]);

  const showNoResults = !isLoading && filteredResults.length === 0;

  return (
    <Combobox value={null} onChange={handleSelect} onClose={() => setIsOpen(false)}>
      <div ref={containerRef} className={`relative ${className || ''}`}>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <SearchIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          </div>
          <ComboboxInput
            className="w-full rounded-full border bg-white py-3 pl-10 pr-4 text-sm leading-5 text-gray-900 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-25"
            displayValue={displayValue}
            onChange={(event) => {
              setSearchText(event.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={t(placeholder)}
            autoComplete="off"
          />
        </div>
        <Transition
          as="div"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="relative"
          show={isOpen}
        >
          <ComboboxOptions className="absolute z-10 mt-2 max-h-80 w-full overflow-auto rounded-lg bg-white py-1.5 ring-1 ring-black ring-opacity-5 focus:outline-none shadow-lg">
            {isLoading ? (
              <div className="relative flex items-center justify-center py-3 px-3.5">
                <Loading loading size={Sizes.sm} isParentSized />
              </div>
            ) : showNoResults ? (
              <div className="relative cursor-default select-none py-2.5 px-3.5 text-sm text-gray-700">
                {t('COMMON.NO_RESULT')}
              </div>
            ) : (
              filteredResults.map((property) => (
                <ComboboxOption
                  key={property.id}
                  className="relative hover:bg-primary/20 group cursor-pointer select-none py-2.5 px-3.5 text-sm transition-colors data-[focus]:bg-primary/10 data-[focus]:text-primary text-gray-900"
                  value={property}
                >
                  {({ selected }) => (
                    <RealEstateSearchBarItem property={property} selected={selected} size="md" />
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </Transition>
      </div>
    </Combobox>
  );
};
